import type { Folder } from '@prisma/client';
import { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDocumentsContext } from '~/react_context/DocumentsContext';

export const AttachmentDragType = {
	FILE: 'file',
	FOLDER: 'folder',
} as const;

export type AttachmentDragItem = { icon: string; id: number; name: string };

type DropState = { isActive: boolean; isValidTarget: boolean };

/** Dashed outline on every valid target once a drag starts, solid on the one being hovered. */
export const dropTargetClass = ({ isActive, isValidTarget }: DropState) =>
	isActive ? 'outline outline-2 outline-primary bg-glazier-blue-50' : isValidTarget ? 'outline-dashed outline-2 outline-primary/50' : '';

const isSelfOrDescendant = ({
	ancestorId,
	folderId,
	folders,
}: {
	ancestorId: number;
	folderId: number;
	folders: Pick<Folder, 'id' | 'parentFolderId'>[];
}) => {
	const parentIdById = new Map(folders.map((folder) => [folder.id, folder.parentFolderId]));
	let id: number | null | undefined = folderId;
	for (let depth = 0; id != null && depth <= folders.length; depth++) {
		if (id === ancestorId) return true;
		id = parentIdById.get(id);
	}
	return false;
};

export const useAttachmentDrop = (targetFolderId: number | null) => {
	const {
		addDocumentToFolderMutation,
		addDocumentsToFolderMutation,
		allFolders,
		checkedDocuments: [checkedDocuments],
		folderId,
		moveFolderMutation,
	} = useDocumentsContext();

	const [dropState, dropRef] = useDrop({
		accept: [AttachmentDragType.FILE, AttachmentDragType.FOLDER],
		canDrop: (item: AttachmentDragItem, monitor) =>
			targetFolderId !== folderId &&
			(monitor.getItemType() === AttachmentDragType.FILE ||
				targetFolderId === null ||
				!isSelfOrDescendant({ ancestorId: item.id, folderId: targetFolderId, folders: allFolders })),
		collect: (monitor): DropState => ({ isActive: monitor.isOver() && monitor.canDrop(), isValidTarget: monitor.canDrop() }),
		drop: (item: AttachmentDragItem, monitor) => {
			if (monitor.getItemType() === AttachmentDragType.FOLDER) {
				moveFolderMutation.mutate({ id: item.id, parentFolderId: targetFolderId });
			} else if (checkedDocuments.includes(item.id)) {
				addDocumentsToFolderMutation.mutate({ documentIds: checkedDocuments, folderId: targetFolderId });
			} else {
				addDocumentToFolderMutation.mutate({ folderId: targetFolderId, id: item.id });
			}
		},
	});

	return [dropState, dropRef] as const;
};

/**
 * Releasing over a non-target cancels the drag, and macOS animates the cancel before firing dragend, so the preview
 * hangs where it was released. Accepting (and ignoring) drops anywhere on the page ends the drag immediately.
 */
export const useDropCatchAll = () => {
	const [, dropRef] = useDrop({ accept: [AttachmentDragType.FILE, AttachmentDragType.FOLDER] });

	return dropRef;
};

/**
 * The browser's own drag image is a screenshot of the card's screen area, so it picks up whatever overlaps the card
 * (the attachment drawer) and renders PDF iframes blank. AttachmentDragLayer draws the preview instead.
 */
export const useAttachmentDrag = ({
	canDrag = true,
	item,
	type,
}: {
	canDrag?: boolean;
	item: AttachmentDragItem;
	type: (typeof AttachmentDragType)[keyof typeof AttachmentDragType];
}) => {
	const [{ isDragging }, dragRef, previewRef] = useDrag({
		canDrag,
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		item,
		type,
	});

	useEffect(() => {
		previewRef(getEmptyImage(), { captureDraggingState: true });
	}, [previewRef]);

	return [{ isDragging }, dragRef] as const;
};
